/**
 * RBAC-3.6 · What a role save does to the people who hold it (FR-13 to FR-16, UX-DR10, UX-DR11).
 *
 * ⚠️ THIS MODULE SHAPES A DIFF, IT DOES NOT DERIVE ONE. The client already holds the before-set and
 * the after-set, so computing gained/lost here is tempting and right most of the time. It stops being
 * right the moment the role is edited concurrently or a grant is refused. `role_save_preview()` and
 * `role_save_permissions()` (`20261101b7`) call ONE function for the diff, so the dialog shows what
 * the save applies. This module turns that answer into words.
 *
 * ⚠️ THE COUNT IS THE DATABASE'S. `holders.count` is the union of `users.role_id` and `user_roles`,
 * active people, in every account the role reaches (a user_roles row in another account still grants
 * what the role carries, because the authority function has no tenant filter). A count from the
 * browser would be whatever RLS shows the caller, which is wrong in the direction of reassurance.
 * `holders.inAccount` is the number the roles list shows; `outsideAccount` is named when it is not 0.
 *
 * Every line is a heading in the display layer's vocabulary (RBAC-2.2) and a sentence from RBAC-2.3's
 * `consequenceFor()`, never a permission key.
 *
 * Pure, no DOM, no Supabase.
 */

import { consequenceFor } from './permissionConsequences';
import { PERMISSION_AREAS, PERMISSION_DISPLAY } from './permissionDisplay';
import { duplicateRoleErrorMessage, type RpcErrorLike } from './roleDuplication';

export const ROLE_HOLDER_COUNT_RPC = 'role_holder_count';
export const ROLE_SAVE_PREVIEW_RPC = 'role_save_preview';
export const ROLE_SAVE_PERMISSIONS_RPC = 'role_save_permissions';

export interface RoleHolders {
  /** Everyone the change reaches, both role systems, any account, active only. */
  count: number;
  /** Of them, in the current account: the number on the roles list. */
  inAccount: number;
  /** Of them, holding the role through another account. Their authority changes too. */
  outsideAccount: number;
  /** The acting user is one of `count`, by either role system. */
  includesCaller: boolean;
}

export interface RoleSaveDiff {
  roleId: number;
  roleName: string;
  gaining: string[];
  losing: string[];
  /** What the acting user stops holding: lost here and not held through another of their roles. */
  youLose: string[];
  holders: RoleHolders;
  saved: boolean;
}

export interface DiffLine {
  /** Stable React key. Never rendered. */
  key: string;
  title: string;
  sentence: string;
}

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const isNames = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');

export function parseRoleHolders(raw: unknown): RoleHolders | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  // active_only is the server saying who it counted. Anything else is a different number.
  if (r.active_only !== true) return null;
  if (!isCount(r.count) || !isCount(r.in_account) || !isCount(r.outside_account)) return null;
  if (typeof r.includes_caller !== 'boolean') return null;
  if (r.in_account + r.outside_account !== r.count) return null;
  return { count: r.count, inAccount: r.in_account, outsideAccount: r.outside_account, includesCaller: r.includes_caller };
}

export function parseRoleSaveDiff(raw: unknown): RoleSaveDiff | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const holders = parseRoleHolders(r.holders);
  if (!holders || !isCount(r.role_id) || typeof r.role_name !== 'string' || typeof r.saved !== 'boolean') return null;
  if (!isNames(r.gaining) || !isNames(r.losing) || !isNames(r.you_lose)) return null;
  // You can only personally lose something this save removes.
  if (!r.you_lose.every((k) => (r.losing as string[]).includes(k))) return null;
  return {
    roleId: r.role_id,
    roleName: r.role_name,
    gaining: r.gaining,
    losing: r.losing,
    youLose: r.you_lose,
    holders,
    saved: r.saved,
  };
}

/** The RBAC-2.2 label, or words that name no key. */
export function permissionTitle(key: string): string {
  return PERMISSION_DISPLAY[key]?.label ?? 'A permission this screen does not show';
}

const areaRank = (key: string) => {
  const a = PERMISSION_DISPLAY[key]?.area;
  const i = a ? PERMISSION_AREAS.indexOf(a) : -1;
  return i < 0 ? PERMISSION_AREAS.length : i;
};

/** Editor order: by area, then by heading, so the diff reads in the order the admin edited. */
function ordered(keys: readonly string[]): string[] {
  return [...keys].sort((a, b) => areaRank(a) - areaRank(b) || permissionTitle(a).localeCompare(permissionTitle(b)));
}

export function diffLines(diff: Pick<RoleSaveDiff, 'gaining' | 'losing' | 'holders'>): { gaining: DiffLine[]; losing: DiffLine[] } {
  const n = diff.holders.count;
  return {
    gaining: ordered(diff.gaining).map((k) => ({ key: k, title: permissionTitle(k), sentence: consequenceFor(k, 'gained', n) })),
    losing: ordered(diff.losing).map((k) => ({ key: k, title: permissionTitle(k), sentence: consequenceFor(k, 'lost', n) })),
  };
}

const people = (n: number) => (n === 1 ? '1 person' : `${n} people`);

/** "A, B and C". */
export function listOf(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** The dialog's subtitle: the role and who holds it, including other accounts when there are any. */
export function diffSubtitle(diff: Pick<RoleSaveDiff, 'roleName' | 'holders'>): string {
  const { count, outsideAccount } = diff.holders;
  const held =
    count === 0 ? 'Nobody holds this role yet.'
      : count === 1 ? '1 person currently holds this role.'
        : `${count} people currently hold this role.`;
  const outside =
    outsideAccount === 0 ? ''
      : outsideAccount === count
        ? ` ${count === 1 ? 'They hold' : 'All of them hold'} it through another account, and this change reaches them there.`
        : ` ${outsideAccount} of them ${outsideAccount === 1 ? 'holds' : 'hold'} it through another account, and this change reaches them there too.`;
  return `${diff.roleName}. ${held}${outside}`;
}

/**
 * FR-15's sentence, and FR-2e's: the handover (`personAccess.handoverPreview`) uses this too, because
 * the two routes to self-demotion must not have different safety. Labels are verb phrases ("See
 * project finance"), so they are quoted to read as names.
 */
export function youWillLoseSentence(keys: readonly string[]): string {
  return `You will lose ${listOf(ordered(keys).map((k) => `“${permissionTitle(k)}”`))}.`;
}

/**
 * FR-15. Null when the acting user does not hold the role. Otherwise always a sentence, because
 * "the diff says so explicitly" holds even when they personally lose nothing.
 */
export function personalCallout(diff: Pick<RoleSaveDiff, 'losing' | 'youLose' | 'holders'>): string | null {
  const { count, includesCaller } = diff.holders;
  if (!includesCaller) return null;
  const who = count === 1 ? 'The only person on this role is you.' : `One of the ${count} is you.`;
  if (diff.youLose.length > 0) return `${who} ${youWillLoseSentence(diff.youLose)}`;
  if (diff.losing.length > 0) {
    return `${who} You keep everything this removes, because another of your roles gives it to you.`;
  }
  return `${who} You lose nothing.`;
}

/** FR-16. The confirm button carries the count and the verb. */
export function confirmLabel(count: number): string {
  if (!isCount(count)) throw new Error(`rolePermissionDiff: ${count} is not a headcount`);
  return count === 0 ? 'Apply to nobody yet' : `Apply to ${people(count)}`;
}

/** Arguments for `role_save_permissions`. `diff.holders.count` is the number on the button. */
export function saveRoleArgs(roleId: number, permissions: readonly string[], base: readonly string[], diff: Pick<RoleSaveDiff, 'holders'>) {
  return {
    p_role_id: roleId,
    p_permission_names: [...new Set(permissions)].sort(),
    p_base_permissions: [...new Set(base)].sort(),
    p_expected_holders: diff.holders.count,
  };
}

/**
 * The server's sentence, verbatim, plus its hint. A 42501 from the reserved-permission trigger names
 * the permission and who may grant it; a 22023 says the role or its people changed since review.
 * Rewording either throws away the part written for the admin. Same rule as the rest of the epic.
 */
export function roleSaveErrorMessage(err: RpcErrorLike | null | undefined): string {
  if (!err?.message?.trim()) return 'The role could not be saved. Check your connection and try again.';
  return duplicateRoleErrorMessage(err);
}

